// About page — all motion lives here. Same flavor as home/motion.js:
//   • fadeUp on labels / h2 / paragraphs
//   • staggerList for grids of cards or logos
//   • mediaScaleEntrance (70% → 100% scale) for standalone images
// No Figma annotations on this page — patterns are the project defaults.
//
// CTA section intentionally left un-animated — see project-voyantis memory:
// any GSAP entrance on .section_global-cta hides the white card on staging.

import {
  ease as resolveEase,
  VOY_OUT_QUART,
  VOY_CUSTOM,
  registerEasings as registerProjectEasings,
  fadeUp,
  staggerList,
  scrollTriggerConfig,
  prefersReducedMotion,
} from '../../motion';

// ─────────────────────────────────────────────────────────────────────────────
// Shared media scale entrance — mirrors the helper in home/motion.js.
// Used for standalone images that should grow from 70% → 100% on entry.
// ─────────────────────────────────────────────────────────────────────────────
function mediaScaleEntrance(selector, fromScale = 0.7) {
  const el = document.querySelector(selector);
  if (!el) return;

  gsap.set(el, { scale: fromScale, transformOrigin: '50% 50%', willChange: 'transform' });

  if (prefersReducedMotion()) {
    gsap.set(el, { scale: 1, clearProps: 'willChange' });
    return;
  }

  gsap.to(el, {
    scale: 1,
    duration: 0.8,
    ease: resolveEase(VOY_CUSTOM),
    scrollTrigger: scrollTriggerConfig({ trigger: el, once: true }),
    onComplete: () => gsap.set(el, { clearProps: 'willChange' }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mission swiper slides reveal — same pattern as the home case-studies cards.
// Cards fade in and slide from x -150 → 0 together. Mission swiper uses
// loop=true, so we clearProps on complete to leave Swiper's measurement of
// slide widths untouched during loop teleports (avoids a visible jump at
// the loop boundary).
// ─────────────────────────────────────────────────────────────────────────────
function missionCardsEntrance() {
  const slides = document.querySelectorAll(
    '.section_about-mission .swiper-slide.is--mission'
  );
  if (!slides.length) return;

  gsap.set(slides, { opacity: 0, x: -150, willChange: 'transform, opacity' });

  if (prefersReducedMotion()) {
    gsap.set(slides, { opacity: 1, x: 0, clearProps: 'willChange' });
    return;
  }

  const tl = gsap.timeline({
    scrollTrigger: scrollTriggerConfig({
      trigger: '.section_about-mission',
      once: true,
    }),
    onComplete: () => gsap.set(slides, { clearProps: 'all' }),
  });

  tl.to(slides, { opacity: 1, duration: 1, ease: 'none' }, 0);
  tl.to(slides, { x: 0, duration: 1, ease: resolveEase(VOY_OUT_QUART) }, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// All section reveals declared together so the relationship between trigger
// elements and their animations is easy to scan.
// ─────────────────────────────────────────────────────────────────────────────
function sectionReveals() {
  // ─── Hero ──────────────────────────────────────────────
  fadeUp('.about-hero_title-label');
  fadeUp('.about-hero_h1', { delay: 0.1 });
  fadeUp('.about-hero_h1-p', { delay: 0.2 });
  mediaScaleEntrance('.about-hero_img-left');
  mediaScaleEntrance('.about-hero_img-right');

  // ─── Principles ────────────────────────────────────────
  // Lower trigger (`top bottom`) so it fires the moment any pixel of the
  // section enters the viewport. Otherwise on viewports where this section
  // sits above the fold at page load, the cards stay stuck at opacity 0
  // because the default `top 75%` never gets crossed via scroll.
  fadeUp('.about-principles_h2', { start: 'top bottom' });
  staggerList('.about-principles_item', { gap: 0.1, start: 'top bottom' });

  // ─── Mission ───────────────────────────────────────────
  fadeUp('.about-mission_title-label');
  fadeUp('.about-mission_h2', { delay: 0.1 });
  fadeUp('.about-mission_p', { delay: 0.2 });
  missionCardsEntrance();

  // ─── Investor / Growth Logos ───────────────────────────
  fadeUp('.growth-logos_h2');
  staggerList('.growth-logo_wrapper', { gap: 0.05 });

  // ─── Careers (reused .section_hp-experts block) ────────
  fadeUp('.hp-experts_h2');
  fadeUp('.hp-experts_p', { delay: 0.1 });
  fadeUp('.hp-experts_layout .secondary-cta-arrow', { delay: 0.2 });
  mediaScaleEntrance('.hp-experts_image');

  // ─── Global CTA ────────────────────────────────────────
  // No entrance — animations on this section hide the white card on staging.
}

// ─────────────────────────────────────────────────────────────────────────────
// Single entry point — called by about/index.js
// ─────────────────────────────────────────────────────────────────────────────
export function initAboutMotion() {
  registerProjectEasings();
  sectionReveals();
}
